// ============================================================
// net-graph.ts — CS:GO-style network graph overlay
// Shows FPS, ping, loss, choke, tick rate, interp delay + mini bar graph
// ============================================================

import { isMobile } from '../core/device';

// ── Colors ──

const COLOR_GREEN = '#4caf50';
const COLOR_YELLOW = '#ffeb3b';
const COLOR_RED = '#f44336';
const COLOR_BG = 'rgba(0, 0, 0, 0.7)';
const COLOR_TEXT = '#e0e0e0';
const COLOR_LABEL = '#9e9e9e';

// ── Thresholds ──

const FPS_GREEN_THRESHOLD = 60; // >60 = green
const FPS_YELLOW_THRESHOLD = 30; // 30-60 = yellow, <30 = red

const PING_GREEN_THRESHOLD = 50; // <50 = green
const PING_YELLOW_THRESHOLD = 100; // 50-100 = yellow, >100 = red

const LOSS_YELLOW_THRESHOLD = 0.01; // >0% = yellow
const LOSS_RED_THRESHOLD = 0.05; // >5% = red

// ── Constants ──

const SERVER_TICK_RATE = 64;
const GRAPH_HISTORY_SIZE = 60; // 60 samples for the mini graph
const MOBILE_GRAPH_HISTORY_SIZE = 30;
const GRAPH_BAR_WIDTH = 3;
const GRAPH_BAR_GAP = 1;
const GRAPH_HEIGHT = 40;
const GRAPH_MAX_PING = 200; // max ping for bar height scaling
const GRAPH_MAX_JITTER = 80;
const GRAPH_MAX_FPS = 120;
const TEXT_UPDATE_INTERVAL_MS = 200;
const GRAPH_UPDATE_INTERVAL_MS = 1000;

type GraphType = 'ping' | 'jitter' | 'fps';
const GRAPH_TYPES: GraphType[] = ['ping', 'jitter', 'fps'];

// ============================================================
// Ring Buffer
// ============================================================

export class RingBuffer<T> {
  private buffer: (T | undefined)[];
  private head = 0;
  private count = 0;
  private readonly capacity: number;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.buffer = new Array(capacity);
  }

  push(value: T): void {
    this.buffer[this.head] = value;
    this.head = (this.head + 1) % this.capacity;
    if (this.count < this.capacity) {
      this.count++;
    }
  }

  size(): number {
    return this.count;
  }

  /** Return items in insertion order (oldest first). */
  toArray(): T[] {
    const result: T[] = [];
    if (this.count === 0) return result;

    const start = this.count < this.capacity
      ? 0
      : this.head; // when full, head points to oldest

    for (let i = 0; i < this.count; i++) {
      const idx = (start + i) % this.capacity;
      result.push(this.buffer[idx] as T);
    }
    return result;
  }

  clear(): void {
    this.head = 0;
    this.count = 0;
    this.buffer = new Array(this.capacity);
  }
}

// ============================================================
// Color helpers (exported for testing)
// ============================================================

export function getColorForFps(fps: number): string {
  if (fps > FPS_GREEN_THRESHOLD) return COLOR_GREEN;
  if (fps >= FPS_YELLOW_THRESHOLD) return COLOR_YELLOW;
  return COLOR_RED;
}

export function getColorForPing(ping: number): string {
  if (ping < PING_GREEN_THRESHOLD) return COLOR_GREEN;
  if (ping <= PING_YELLOW_THRESHOLD) return COLOR_YELLOW;
  return COLOR_RED;
}

export function getColorForLoss(loss: number): string {
  if (loss <= 0) return COLOR_GREEN;
  if (loss <= LOSS_RED_THRESHOLD) return COLOR_YELLOW;
  return COLOR_RED;
}

// ============================================================
// Format helpers (exported for testing)
// ============================================================

export function formatMs(value: number): string {
  return `${Math.round(value)} ms`;
}

export function formatFps(value: number): string {
  return `${Math.round(value)}`;
}

export function formatPercent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

// ============================================================
// Data snapshot
// ============================================================

export interface NetGraphData {
  fps: number;
  ping: number;
  jitter: number;
  loss: number;
  choke: number;
  tickRate: number;
  interp: number;
}

// ============================================================
// NetGraphDataCollector — gathers stats from game systems
// ============================================================

export class NetGraphDataCollector {
  private _fps = 0;
  private _ping = 0;
  private _jitter = 0;
  private _loss = 0;
  private _choke = 0;
  private _interp = 0;

  private pingHistory: RingBuffer<number>;
  private jitterHistory: RingBuffer<number>;
  private fpsHistory: RingBuffer<number>;
  private lossHistory: RingBuffer<number>;

  constructor(historySize: number = GRAPH_HISTORY_SIZE) {
    this.pingHistory = new RingBuffer<number>(historySize);
    this.jitterHistory = new RingBuffer<number>(historySize);
    this.fpsHistory = new RingBuffer<number>(historySize);
    this.lossHistory = new RingBuffer<number>(historySize);
  }

  recordFps(fps: number): void {
    this._fps = fps;
  }

  recordPing(ping: number): void {
    if (this._ping > 0) {
      this._jitter = Math.abs(ping - this._ping);
    }
    this._ping = ping;
  }

  recordLoss(loss: number): void {
    this._loss = loss;
  }

  recordChoke(choke: number): void {
    this._choke = choke;
  }

  recordInterp(interp: number): void {
    this._interp = interp;
  }

  /** Push the current ping and loss values into the history ring buffers. */
  pushHistorySample(): void {
    this.pingHistory.push(this._ping);
    this.jitterHistory.push(this._jitter);
    this.fpsHistory.push(this._fps);
    this.lossHistory.push(this._loss);
  }

  getLatest(): NetGraphData {
    return {
      fps: this._fps,
      ping: this._ping,
      jitter: this._jitter,
      loss: this._loss,
      choke: this._choke,
      tickRate: SERVER_TICK_RATE,
      interp: this._interp,
    };
  }

  getPingHistory(): number[] {
    return this.pingHistory.toArray();
  }

  getJitterHistory(): number[] {
    return this.jitterHistory.toArray();
  }

  getFpsHistory(): number[] {
    return this.fpsHistory.toArray();
  }

  getLossHistory(): number[] {
    return this.lossHistory.toArray();
  }
}

// ============================================================
// NetGraph — DOM overlay
// ============================================================

export class NetGraph {
  private container: HTMLDivElement | null = null;
  private visible = false;
  private collector: NetGraphDataCollector;
  private readonly graphHistorySize: number;

  // DOM elements for text rows
  private fpsCounterEl: HTMLDivElement | null = null;
  private pingValue: HTMLSpanElement | null = null;
  private jitterValue: HTMLSpanElement | null = null;
  private lossValue: HTMLSpanElement | null = null;
  private chokeValue: HTMLSpanElement | null = null;
  private tickValue: HTMLSpanElement | null = null;
  private interpValue: HTMLSpanElement | null = null;
  private graphSelectorEl: HTMLButtonElement | null = null;
  private graphType: GraphType = 'ping';

  // Mini graph canvas
  private graphCanvas: HTMLCanvasElement | null = null;
  private graphCtx: CanvasRenderingContext2D | null = null;

  // Timing
  private lastTextUpdate = 0;
  private lastGraphUpdate = 0;
  private lastFrameTime = 0;
  private frameDeltaFps = 0;

  constructor() {
    this.graphHistorySize = isMobile() ? MOBILE_GRAPH_HISTORY_SIZE : GRAPH_HISTORY_SIZE;
    this.collector = new NetGraphDataCollector(this.graphHistorySize);
    this.createDOM();
  }

  // ── Public API ──

  isVisible(): boolean {
    return this.visible;
  }

  show(): void {
    this.visible = true;
    this.lastFrameTime = 0;
    if (this.container) {
      this.container.style.display = 'block';
    }
  }

  hide(): void {
    this.visible = false;
    this.lastFrameTime = 0;
    if (this.container) {
      this.container.style.display = 'none';
    }
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  getDataCollector(): NetGraphDataCollector {
    return this.collector;
  }

  /** Call every frame (or at desired update rate). Uses internal throttle. */
  update(): void {
    if (!this.visible) return;

    const now = performance.now();
    if (this.lastFrameTime > 0) {
      const deltaMs = now - this.lastFrameTime;
      if (deltaMs > 0) {
        const instantFps = 1000 / deltaMs;
        this.frameDeltaFps = this.frameDeltaFps === 0
          ? instantFps
          : this.frameDeltaFps * 0.85 + instantFps * 0.15;
        this.collector.recordFps(this.frameDeltaFps);
      }
    }
    this.lastFrameTime = now;

    // Update text at 5Hz (200ms)
    if (now - this.lastTextUpdate >= TEXT_UPDATE_INTERVAL_MS) {
      this.lastTextUpdate = now;
      this.updateText();
    }

    // Update graph at 1Hz (1000ms)
    if (now - this.lastGraphUpdate >= GRAPH_UPDATE_INTERVAL_MS) {
      this.lastGraphUpdate = now;
      this.collector.pushHistorySample();
      this.updateGraph();
    }
  }

  dispose(): void {
    this.visible = false;
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    this.fpsCounterEl = null;
    this.pingValue = null;
    this.jitterValue = null;
    this.lossValue = null;
    this.chokeValue = null;
    this.tickValue = null;
    this.interpValue = null;
    this.graphSelectorEl = null;
    this.graphCanvas = null;
    this.graphCtx = null;
  }

  // ── DOM construction ──

  private createDOM(): void {
    const mobile = isMobile();
    this.container = document.createElement('div') as unknown as HTMLDivElement;
    Object.assign(this.container.style, {
      position: 'fixed',
      right: '8px',
      ...(mobile ? { top: '160px' } : { bottom: '8px' }),
      zIndex: '9999',
      background: COLOR_BG,
      borderRadius: '4px',
      padding: '8px 12px',
      fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", "Monaco", monospace',
      fontSize: '11px',
      lineHeight: '1.6',
      color: COLOR_TEXT,
      pointerEvents: 'auto',
      userSelect: 'none',
      display: 'none', // starts hidden
      minWidth: '160px',
    });

    this.fpsCounterEl = document.createElement('div') as unknown as HTMLDivElement;
    this.fpsCounterEl.textContent = 'FPS: --';
    Object.assign(this.fpsCounterEl.style, {
      fontSize: '11px',
      fontWeight: 'bold',
      color: COLOR_TEXT,
      marginBottom: '3px',
    });
    this.container.appendChild(this.fpsCounterEl as any);

    // Text rows
    const rows: Array<{ label: string; ref: 'pingValue' | 'jitterValue' | 'lossValue' | 'chokeValue' | 'tickValue' | 'interpValue' }> = [
      { label: 'Ping', ref: 'pingValue' },
      { label: 'Jitter', ref: 'jitterValue' },
      { label: 'Loss', ref: 'lossValue' },
      { label: 'Choke', ref: 'chokeValue' },
      { label: 'Tick', ref: 'tickValue' },
      { label: 'Interp', ref: 'interpValue' },
    ];

    for (const row of rows) {
      const line = document.createElement('div');
      Object.assign(line.style, {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
      });

      const label = document.createElement('span');
      label.textContent = row.label;
      Object.assign(label.style, { color: COLOR_LABEL });

      const value = document.createElement('span');
      value.textContent = '--';

      line.appendChild(label);
      line.appendChild(value);
      this.container.appendChild(line);

      (this as any)[row.ref] = value;
    }

    // Separator
    const sep = document.createElement('div');
    Object.assign(sep.style, {
      height: '1px',
      background: 'rgba(255,255,255,0.1)',
      margin: '4px 0',
    });
    this.container.appendChild(sep);

    this.graphSelectorEl = document.createElement('button');
    this.graphSelectorEl.type = 'button';
    Object.assign(this.graphSelectorEl.style, {
      width: '100%',
      margin: '0 0 4px',
      padding: '2px 4px',
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: '3px',
      color: COLOR_TEXT,
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '10px',
      textAlign: 'left',
    });
    // Test mocks createElement('button') without addEventListener support;
    // guard so the constructor still works under the node-env stub.
    if (typeof this.graphSelectorEl.addEventListener === 'function') {
      this.graphSelectorEl.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.cycleGraphType();
      });
    }
    this.updateGraphSelectorText();
    this.container.appendChild(this.graphSelectorEl);

    // Mini bar graph canvas
    const canvasWidth = this.graphHistorySize * (GRAPH_BAR_WIDTH + GRAPH_BAR_GAP);
    this.graphCanvas = document.createElement('canvas') as unknown as HTMLCanvasElement;
    (this.graphCanvas as any).width = canvasWidth;
    (this.graphCanvas as any).height = GRAPH_HEIGHT;
    Object.assign(this.graphCanvas.style, {
      width: `${canvasWidth}px`,
      height: `${GRAPH_HEIGHT}px`,
      display: 'block',
    });

    // getContext may not exist in test environment
    if (typeof (this.graphCanvas as any).getContext === 'function') {
      this.graphCtx = (this.graphCanvas as any).getContext('2d');
    }

    this.container.appendChild(this.graphCanvas as any);

    document.body.appendChild(this.container as any);
  }

  private cycleGraphType(): void {
    const idx = GRAPH_TYPES.indexOf(this.graphType);
    this.graphType = GRAPH_TYPES[(idx + 1) % GRAPH_TYPES.length];
    this.updateGraphSelectorText();
    this.updateGraph();
  }

  private updateGraphSelectorText(): void {
    if (!this.graphSelectorEl) return;
    const label = this.graphType === 'fps' ? 'FPS' : this.graphType[0].toUpperCase() + this.graphType.slice(1);
    this.graphSelectorEl.textContent = `Graph: ${label}`;
  }

  // ── Text updates ──

  private updateText(): void {
    const data = this.collector.getLatest();
    const fps = this.frameDeltaFps > 0 ? this.frameDeltaFps : data.fps;

    if (this.fpsCounterEl) {
      this.fpsCounterEl.textContent = `FPS: ${formatFps(fps)}`;
      this.fpsCounterEl.style.color = getColorForFps(fps);
    }

    if (this.pingValue) {
      this.pingValue.textContent = formatMs(data.ping);
      this.pingValue.style.color = getColorForPing(data.ping);
    }

    if (this.jitterValue) {
      this.jitterValue.textContent = formatMs(data.jitter);
      this.jitterValue.style.color = getColorForPing(data.jitter);
    }

    if (this.lossValue) {
      this.lossValue.textContent = formatPercent(data.loss);
      this.lossValue.style.color = getColorForLoss(data.loss);
    }

    if (this.chokeValue) {
      this.chokeValue.textContent = formatPercent(data.choke);
      this.chokeValue.style.color = getColorForLoss(data.choke);
    }

    if (this.tickValue) {
      this.tickValue.textContent = `${data.tickRate}`;
      this.tickValue.style.color = COLOR_GREEN;
    }

    if (this.interpValue) {
      this.interpValue.textContent = formatMs(data.interp);
      this.interpValue.style.color = data.interp > 100 ? COLOR_YELLOW : COLOR_GREEN;
    }
  }

  // ── Graph rendering ──

  private updateGraph(): void {
    if (!this.graphCtx || !this.graphCanvas) return;

    const ctx = this.graphCtx;
    const width = (this.graphCanvas as any).width as number;
    const height = (this.graphCanvas as any).height as number;

    // Clear
    ctx.clearRect(0, 0, width, height);

    const lossHistory = this.collector.getLossHistory();
    const graphHistory = this.getCurrentGraphHistory();
    const sampleCount = graphHistory.length;

    for (let i = 0; i < sampleCount; i++) {
      const x = i * (GRAPH_BAR_WIDTH + GRAPH_BAR_GAP);

      const value = graphHistory[i];
      const barNorm = Math.min(value / this.getCurrentGraphMax(), 1);
      const barH = barNorm * height;
      ctx.fillStyle = this.getCurrentGraphColor(value);
      ctx.fillRect(x, height - barH, GRAPH_BAR_WIDTH, barH);

      // Loss spike overlay remains on the ping graph as a quick packet-loss cue.
      if (this.graphType === 'ping' && i < lossHistory.length && lossHistory[i] > 0) {
        const lossNorm = Math.min(lossHistory[i], 1);
        const barH = lossNorm * height;
        ctx.fillStyle = 'rgba(244, 67, 54, 0.8)';
        ctx.fillRect(x, height - barH, GRAPH_BAR_WIDTH, barH);
      }
    }
  }

  private getCurrentGraphHistory(): number[] {
    if (this.graphType === 'jitter') return this.collector.getJitterHistory();
    if (this.graphType === 'fps') return this.collector.getFpsHistory();
    return this.collector.getPingHistory();
  }

  private getCurrentGraphMax(): number {
    if (this.graphType === 'jitter') return GRAPH_MAX_JITTER;
    if (this.graphType === 'fps') return GRAPH_MAX_FPS;
    return GRAPH_MAX_PING;
  }

  private getCurrentGraphColor(value: number): string {
    if (this.graphType === 'fps') return getColorForFps(value);
    return getColorForPing(value);
  }
}
